import { useEffect, useRef, useCallback } from 'react';
import { fetchUserPreferences, updateUserPreferences } from '@/features/edgemail-client/services/MailClientAPI';
import { useUserPreferencesStore } from '@/store/userPreferencesStore';
import {
  parseHexColor,
  parseRgbString,
  contrastRatio,
  WCAG_AA_NORMAL,
} from '@/utils/contrastRatio';
import {
  applyThemeModeToDocument,
  getResolvedThemeMode,
  THEME_MEDIA_QUERY,
} from '@/utils/themeMode';

/** Enable smooth color transitions briefly during theme/accent switches, then remove */
const enableThemeTransition = () => {
  const root = document.documentElement;
  root.classList.add('lex-transitioning');
  // Remove after transitions complete (match --brand-transition-medium 220ms + buffer)
  setTimeout(() => root.classList.remove('lex-transitioning'), 350);
};

/**
 * Apply (or clear) a single user font-color override on :root.
 * Sets the CSS variable + tagging attribute when the value passes WCAG AA OR
 * the force flag is true. Removes both when the value is null/invalid/failing.
 * Used by useApplyAppearancePreferences to drive heading + body pickers
 * symmetrically with independent contrast gates.
 */
const applyFontColorOverride = (
  root: HTMLElement,
  cssVar: string,
  attr: string,
  color: string | null | undefined,
  force: boolean | undefined,
  bgRgb: ReturnType<typeof parseRgbString>,
): void => {
  const fgRgb = color ? parseHexColor(color) : null;
  if (fgRgb && bgRgb) {
    const ratio = contrastRatio(fgRgb, bgRgb);
    const passes = ratio >= WCAG_AA_NORMAL;
    if (passes || force) {
      root.style.setProperty(cssVar, color!);
      root.setAttribute(attr, 'user');
      return;
    }
  }
  root.style.removeProperty(cssVar);
  root.removeAttribute(attr);
};

export function useApplyAppearancePreferences(): void {
  const appearance = useUserPreferencesStore((state) => state.appearancePreferences);
  const isFirstRender = useRef(true);

  useEffect(() => {
    const root = document.documentElement;
    // Enable smooth transition on theme switch (skip first render to avoid flash)
    if (!isFirstRender.current) enableThemeTransition();
    const resolved = getResolvedThemeMode(appearance.themeMode);
    applyThemeModeToDocument(resolved);

    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('webelieve-theme-change', { detail: appearance.themeMode }));
    }

    if (appearance.themeMode === 'system') {
      const media = window.matchMedia(THEME_MEDIA_QUERY);
      const handler = (event: MediaQueryListEvent | MediaQueryList) => {
        applyThemeModeToDocument(event.matches ? 'dark' : 'light');
      };
      handler(media);
      media.addEventListener('change', handler as EventListener);
      return () => media.removeEventListener('change', handler as EventListener);
    }

    return undefined;
  }, [appearance.themeMode]);

  useEffect(() => {
    const root = document.documentElement;
    // Enable smooth transition on accent/neutral/density change (skip first render)
    if (!isFirstRender.current) enableThemeTransition();
    root.setAttribute('data-accent', appearance.accentColor);
    root.setAttribute('data-neutral', appearance.neutralBase || 'zinc');
    root.setAttribute('data-density', appearance.density);
    root.setAttribute('data-contrast', appearance.contrastMode);

    const customAccent = appearance.accentCustomHex || '#3b82f6';
    root.style.setProperty('--brand-accent-custom', customAccent);

    const scale = Number.isFinite(appearance.fontScale) ? appearance.fontScale : 1;
    root.style.setProperty('--brand-font-scale', `${scale}`);
    root.style.fontSize = `${16 * scale}px`;

    // Apply per-category font-color overrides. Each picker (heading + body)
    // is independently validated against the live body background and either
    // applied or skipped. When applied, the corresponding CSS variable is set
    // on :root and a data-text-override-* attribute is tagged so the CSS
    // derivation rules in brand-theme.css can fan out tertiary/muted from the
    // appropriate source. Failing values stay in storage but are not applied;
    // the app falls back to theme defaults. The modal escape-zone CSS is the
    // other half of the lockout-prevention contract.
    //
    // No-regressions: when only fontCustomColor (heading) is set and
    // fontBodyColor is null, the CSS rule
    //   [data-text-override-heading="user"]:not([data-text-override-body="user"])
    // derives secondary/tertiary/muted from primary — preserving v1 behavior.
    const bgRgb = parseRgbString(getComputedStyle(document.body).backgroundColor);
    applyFontColorOverride(
      root,
      '--brand-text-primary',
      'data-text-override-heading',
      appearance.fontCustomColor,
      appearance.fontCustomColorForce,
      bgRgb,
    );
    applyFontColorOverride(
      root,
      '--brand-text-secondary',
      'data-text-override-body',
      appearance.fontBodyColor,
      appearance.fontBodyColorForce,
      bgRgb,
    );

    // Remove loading guard after first paint
    requestAnimationFrame(() => {
      root.classList.remove('lex-loading');
      isFirstRender.current = false;
    });
  }, [appearance.accentColor, appearance.accentCustomHex, appearance.neutralBase, appearance.density, appearance.contrastMode, appearance.fontScale, appearance.fontCustomColor, appearance.fontCustomColorForce, appearance.fontBodyColor, appearance.fontBodyColorForce, appearance.themeMode]);
}

export function useUserPreferencesSync(): void {
  const {
    appearancePreferences,
    notificationPreferences,
    appPreferences,
    setAllPreferences,
    hasHydrated,
    markHydrated,
  } = useUserPreferencesStore();

  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const idleHandle = useRef<number | null>(null);
  const lastSaved = useRef<string>('');
  const pendingSync = useRef<boolean>(false);

  useEffect(() => {
    let cancelled = false;

    const hydrate = async () => {
      // Signal ready immediately so consumers can load in parallel with this fetch.
      // Zustand persist already restored localStorage values synchronously.
      if (!cancelled) markHydrated();
      try {
        const prefs = await fetchUserPreferences();
        if (cancelled || !prefs) return;
        setAllPreferences(prefs);
        lastSaved.current = JSON.stringify(prefs);
      } catch {
        // Offline or server unavailable; fall back to persisted state
      }
    };

    hydrate();

    return () => {
      cancelled = true;
    };
  }, [setAllPreferences, markHydrated]);

  useEffect(() => {
    if (!hasHydrated) return;

    const payload = {
      appearancePreferences,
      notificationPreferences,
      appPreferences,
    };

    const serialized = JSON.stringify(payload);
    if (serialized === lastSaved.current) return;

    if (saveTimer.current) {
      clearTimeout(saveTimer.current);
    }

    const win = window as Window & {
      requestIdleCallback?: (cb: (deadline: { didTimeout: boolean; timeRemaining: () => number }) => void, opts?: { timeout: number }) => number;
      cancelIdleCallback?: (handle: number) => void;
    };

    if (idleHandle.current && win.cancelIdleCallback) {
      win.cancelIdleCallback(idleHandle.current);
    }

    const persist = () => {
      if (!navigator.onLine) {
        pendingSync.current = true;
        return;
      }

      updateUserPreferences(payload)
        .then(() => {
          lastSaved.current = serialized;
          pendingSync.current = false;
        })
        .catch(() => {
          pendingSync.current = true;
        });
    };

    saveTimer.current = setTimeout(() => {
      if (win.requestIdleCallback) {
        idleHandle.current = win.requestIdleCallback(persist, { timeout: 2000 });
      } else {
        persist();
      }
    }, 700);

    return () => {
      if (saveTimer.current) {
        clearTimeout(saveTimer.current);
      }
      if (idleHandle.current && win.cancelIdleCallback) {
        win.cancelIdleCallback(idleHandle.current);
      }
    };
  }, [appearancePreferences, notificationPreferences, appPreferences, hasHydrated]);

  useEffect(() => {
    const handleOnline = () => {
      if (!pendingSync.current) return;
      const payload = {
        appearancePreferences,
        notificationPreferences,
        appPreferences,
      };
      updateUserPreferences(payload)
        .then(() => {
          lastSaved.current = JSON.stringify(payload);
          pendingSync.current = false;
        })
        .catch(() => {
          pendingSync.current = true;
        });
    };

    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, [appearancePreferences, notificationPreferences, appPreferences]);
}
